import { createContext, useContext } from "react";

import type { UseFormReturn } from "react-hook-form";

import type { Country } from "../../../AddressForm/AddressForm.types";
import type { SelectOption } from "../../../app-main/types/general.types";
import type { TaxRegistration } from "../../TaxDetails/TaxDetails.types";
import type { TaxDetailsFormValues } from "../TaxDetailsFormSection.types";

/** Everything the tax details form tree needs, derived once in the provider. */
type TaxDetailsFormContextType = {
  countryOptions: SelectOption[];
  form: UseFormReturn<TaxDetailsFormValues>;
  /** Whether the selected region levies the tax this form is scoped to. */
  hasScopedTax: boolean;
  /** True once the account is pinned to a country, which the form cannot change. */
  isCountryLocked: boolean;
  /** True when a registration already exists for this scope. */
  isEdit: boolean;
  /** Drives the rate field, which only a host fills in. */
  isHost: boolean;
  /** True when a country is chosen but its province is not. */
  isRegionRequired: boolean;
  isSubregion: boolean;
  /** The country's own word for its subdivision, lowercased: "province". */
  regionLabel: string;
  /** The province, or the country when none is picked — names the empty state. */
  regionName: string;
  /** The registration this form is for; absent until one exists. */
  registration?: TaxRegistration;
  /** Runs the submit and reports a server message back onto the form. */
  submit: (values: TaxDetailsFormValues) => Promise<void>;
  /** The country the form currently has selected, in the reader's own language. */
  selectedCountry?: Country;
  /**
   * Names the tax throughout the form — already translated, so it goes straight
   * into a `{taxType}` placeholder. "VAT", "GST/HST", "Sales tax".
   */
  taxTypeName: string;
  zoneOptions: SelectOption[];
};

const TaxDetailsFormContext = createContext<TaxDetailsFormContextType | null>(
  null,
);

/**
 * Reads the tax details form context.
 *
 * @throws When called outside of a `TaxDetailsFormProvider`.
 */
const useTaxDetailsForm = () => {
  const context = useContext(TaxDetailsFormContext);

  if (null === context) {
    throw new Error(
      "useTaxDetailsForm must be used within a TaxDetailsFormProvider.",
    );
  }

  return context;
};

export { TaxDetailsFormContext, useTaxDetailsForm };

export type { TaxDetailsFormContextType };
